import React, { useContext, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useAppState } from './context/AppStateContext';
import TopBar from './components/TopBar';
import Sidebar from './components/LeftSidebar';
import RightSidebar from './components/RightSidebar';
import Home from './pages/Home';
import Deeds from './pages/Deeds';
import Training from './pages/Training';
import Quests from './pages/Quests';
import Chat from './pages/Chat';
import City from './pages/City';
import Travel from './pages/Travel';
import Profile from './pages/Profile';
import EditProfile from './pages/EditProfile';
import CharacterCreation from './pages/CharacterCreation';
import AdminDashboard from './pages/AdminDashboard';
import ManageHome from './pages/admin/ManageHome';
import ManageUsers from './pages/admin/ManageUsers';
import ManageCharacters from './pages/admin/ManageCharacters';
import ManageDeeds from './pages/admin/ManageDeeds';
import ManageSkills from './pages/admin/ManageSkills';
import ManageQuests from './pages/admin/ManageQuests';
import ManageCities from './pages/admin/ManageCities';
import ManageGuilds from './pages/admin/ManageGuilds';
import ManageChat from './pages/admin/ManageChat';
import ManageTickerFeed from './pages/admin/ManageTickerFeed';
import { UserProvider, UserContext } from './context/UserContext';
import RequireCharacter from './components/RequireCharacter';
import CharacterSearch from './pages/CharacterSearch';
import PlayerCharacter from './pages/PlayerCharacter';
import { AppStateProvider } from './context/AppStateContext';
import { AuthProvider } from './context/AuthContext';
import './styles.css';

// Main App component that sets up providers
function App() {
  return (
    <Router>
      <AuthProvider>
        <AppStateProvider>
          <UserProvider>
            <AppWithCooldowns />
          </UserProvider>
        </AppStateProvider>
      </AuthProvider>
    </Router>
  );
}

// Update the AppWithCooldowns component with a better dependency tracking

// New intermediate component that can safely use useAppState
const AppWithCooldowns = () => {
  const { refreshCooldowns } = useAppState();
  const didInitialize = React.useRef(false);
  
  // Use a layout effect to run once before rendering
  // This avoids the dependency warning and ensures it runs just once
  useLayoutEffect(() => {
    if (!didInitialize.current) {
      console.log('Running initial cooldown refresh');
      refreshCooldowns();
      didInitialize.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally omit refreshCooldowns from deps to prevent re-runs
  
  return <AppContent />;
};

// The original AppContent component remains the same
const AppContent = () => {
  const { loading } = useContext(UserContext);
  
  if (loading) {
    return <div className="loading">Loading...</div>;
  }
  
  return (
    <div className="app-container">
      <TopBar />
      <div className="main-content">
        <Sidebar />
        <div className="center-content">
          <Routes>
            <Route path="/character/create" element={<CharacterCreation />} />
            <Route element={<RequireCharacter />}>
              <Route path="/" element={<Home />} />
              <Route path="/home" element={<Home />} />
              <Route path="/deeds" element={<Deeds />} />
              <Route path="/training" element={<Training />} />
              <Route path="/quests" element={<Quests />} />
              <Route path="/chat" element={<Chat />} />
              <Route path="/city" element={<City />} />
              <Route path="/travel" element={<Travel />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/edit-profile" element={<EditProfile />} />
              <Route path="/character/search" element={<CharacterSearch />} />
              <Route path="/character/view/:id" element={<PlayerCharacter />} />
              
              {/* Admin routes - only accessible via AdminDashboard which has its own protection */}
              <Route path="/admin" element={<AdminDashboard />}>
                <Route path="manage-home" element={<ManageHome />} />
                <Route path="manage-users" element={<ManageUsers />} />
                <Route path="manage-characters" element={<ManageCharacters />} />
                <Route path="manage-deeds" element={<ManageDeeds />} />
                <Route path="manage-skills" element={<ManageSkills />} />
                <Route path="manage-quests" element={<ManageQuests />} />
                <Route path="manage-cities" element={<ManageCities />} />
                <Route path="manage-guilds" element={<ManageGuilds />} />
                <Route path="manage-chat" element={<ManageChat />} />
                <Route path="manage-ticker" element={<ManageTickerFeed />} />
              </Route>
            </Route>
          </Routes>
        </div>
        <RightSidebar />
      </div>
    </div>
  );
};

export default App;
